import Button from '@atlaskit/button';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { N30 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { defaultStoreUtils } from '@databases/redux';
import { DistributiveOmit } from '@databases/types';
import { DatabaseField } from '@databases/types/database';
import { DatabaseFilter } from '@databases/types/database/view';
import { getSupportedFilterTypes } from '@databases/utils/database/field-types';
import {
  getDatabaseFieldOptional,
  getDatabaseFields,
} from '@databases/utils/database/utils';
import { ViewChangeType } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { FunctionComponent, useCallback, useLayoutEffect, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { getPanelElement } from 'react-resizable-panels';

import { useFilterSettings } from '../../hooks/useFilterSettings';
import { messages } from '../../i18n';

export type FilterType = DistributiveOmit<DatabaseFilter, 'value'>;

const borderBoxStyles = xcss({
  borderBottom: `2px solid ${token('color.border', N30)}`,
});

export interface ViewSettingsFilterRowProps {
  contentId: string;
  value: DatabaseFilter;
  onChange: (change: ViewChangeType) => void;
  onRemove: () => void;
  autoFocusValue?: boolean;
  FieldPicker: React.FunctionComponent<any>;
  FilterTypePicker: React.FunctionComponent<any>;
  FilterValueEdit: React.FunctionComponent<any>;
}

const ViewSettingsFilterRow: FunctionComponent<ViewSettingsFilterRowProps> = ({
  contentId,
  value,
  onChange,
  onRemove,
  autoFocusValue,
  FieldPicker,
  FilterTypePicker: TypePicker,
  FilterValueEdit: ValueEdit,
}) => {
  const store = defaultStoreUtils.useStore();
  const { formatMessage } = useIntl();
  const { handleFieldChange, handleFilterTypeChange, handleFilterValueChange } =
    useFilterSettings({
      onChange,
      filterObject: value,
      contentId,
      store,
    });

  const [fields, field] = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    return [
      getDatabaseFields(database),
      getDatabaseFieldOptional(database, (value as any).fieldId),
    ] as const;
  });

  const types = useMemo(
    () => (field ? getSupportedFilterTypes(field.type) : undefined),
    [field]
  );

  const handleFieldChangeWithEvent = useCallback(
    (field: DatabaseField | undefined) => {
      handleFieldChange(field, 'databaseViewSettingsPanel');
    },
    [handleFieldChange]
  );

  const [panelWidth, setPanelWidth] = React.useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const panelElement = getPanelElement('right-side-panel');
    const resizeObserver = new ResizeObserver(() => {
      setPanelWidth(panelElement?.getBoundingClientRect().width);
    });
    if (panelElement) {
      resizeObserver.observe(panelElement);
    }

    return () => {
      if (panelElement) {
        resizeObserver.unobserve(panelElement);
      }
    };
  }, []);

  const filterFieldPicker = useMemo(() => {
    return (
      <FieldPicker
        fields={fields}
        value={field}
        onChange={handleFieldChangeWithEvent}
        // After clicking "Add", a new filter row is created with no field selected. The field picker should be focused.
        autoFocus={!autoFocusValue && !field}
        placeholder={formatMessage(
          messages['view-settings-panel-filter.select-field-placeholder']
        )}
      />
    );
  }, [
    FieldPicker,
    autoFocusValue,
    field,
    fields,
    formatMessage,
    handleFieldChangeWithEvent,
  ]);

  const filterTypePicker = useMemo(() => {
    return (
      <TypePicker
        contentId={contentId}
        value={value}
        onChange={handleFilterTypeChange}
        types={types}
      />
    );
  }, [TypePicker, contentId, handleFilterTypeChange, types, value]);

  const filterValueEdit = useMemo(() => {
    return (
      <ValueEdit
        contentId={contentId}
        value={value}
        onChange={handleFilterValueChange}
        // When adding a filter through the field actions, the field value should be focused
        autoFocus={autoFocusValue}
        appearance="default"
      />
    );
  }, [ValueEdit, autoFocusValue, contentId, handleFilterValueChange, value]);

  const filterRow = useMemo(() => {
    if (panelWidth && panelWidth >= 480) {
      return (
        <Inline space="space.050" alignBlock="center" testId="one-line-filter-row">
          <Box xcss={xcss({ width: '35%' })}>{filterFieldPicker}</Box>
          <Box xcss={xcss({ width: '25%' })}>{filterTypePicker}</Box>
          <Box xcss={xcss({ width: '40%' })}>{filterValueEdit}</Box>
        </Inline>
      );
    } else {
      return (
        <Stack space="space.100" testId="two-line-filter-row">
          <Inline space="space.050">
            <Box xcss={xcss({ width: '58%' })}>{filterFieldPicker}</Box>
            <Box xcss={xcss({ width: '42%' })}>{filterTypePicker}</Box>
          </Inline>
          <Box>{filterValueEdit}</Box>
        </Stack>
      );
    }
  }, [filterFieldPicker, filterTypePicker, filterValueEdit, panelWidth]);

  return (
    <Stack
      space="space.200"
      xcss={!!panelWidth && panelWidth < 480 && xcss({ paddingTop: 'space.100' })}
    >
      <Inline space="space.100">
        <Box xcss={xcss({ width: '97%' })}>{filterRow}</Box>
        <Box xcss={xcss({ marginTop: 'space.050' })}>
          <Tooltip
            content={formatMessage(
              messages['view-settings-panel-filter.remove-filter-label']
            )}
          >
            <Button
              appearance="subtle"
              iconBefore={
                <TrashIcon
                  size="small"
                  label={formatMessage(
                    messages['view-settings-panel-filter.remove-filter-label']
                  )}
                />
              }
              onClick={onRemove}
            />
          </Tooltip>
        </Box>
      </Inline>
      {panelWidth && panelWidth < 480 && (
        <Box testId="filter-row-border" xcss={borderBoxStyles} />
      )}
    </Stack>
  );
};

export default ViewSettingsFilterRow;
